import { Dialog, styled } from '@material-ui/core';
import { GestureEvent } from 'Events/withEvent';
import React from 'react';
import { LAIZE_CALC_DIALOG_HEADER_HEIGHT } from '../LaizeCalcDialogHeader';
import { ButtonClose } from 'Web/Components/Widgets/ButtonClose';
import { useViewState } from 'Web/Hooks/ViewState/ViewState';
import { BoundingBox, CoordPoint } from 'Plugins/FloorPlan/Utils/Types';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';
import { LegendConstants } from './Widget';

type BandsDialogProps = { open: boolean; close: VoidFunction } & React.HtmlHTMLAttributes<HTMLDivElement>;

export const BandsDialog = ({ open, close, children, ...attrs }: React.PropsWithChildren<BandsDialogProps>) => {
    const onClose = (e: GestureEvent) => {
        e.stopPropagation();
        close();
    };

    return (
        <BandsDialogProvider>
            <Dialog
                {...attrs}
                open={open}
                onClose={onClose}
                fullScreen
                BackdropProps={{ style: { top: LAIZE_CALC_DIALOG_HEADER_HEIGHT } }}
                style={{
                    width: '84%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    paddingTop: LAIZE_CALC_DIALOG_HEADER_HEIGHT + 86,
                    paddingBottom: 125,
                }}>
                <Body role="Body">
                    {children}
                    <ButtonClose
                        onClick={onClose}
                        style={{ width: 50, height: 50, position: 'absolute', top: 0, right: 0 }}
                    />
                </Body>
            </Dialog>
        </BandsDialogProvider>
    );
};

const Body = styled('div')({ width: '100%', height: '100%', position: 'relative' });

type BandsDialogState = { 
    /** bandId, stairId, ... */
    id?: string
};

type BandsDialogValue = {
    state: BandsDialogState;
    update: (values: BandsDialogState) => void;
};
const BandsDialogContext = React.createContext<BandsDialogValue>({ state: {}, update: () => {} });
export const usePlanBandeDialog = () => React.useContext(BandsDialogContext);
export const usePlanBandeDialogState = () => React.useContext(BandsDialogContext).state;
export const usePlanBandeDialogUpdate = () => React.useContext(BandsDialogContext).update;
export const usePlanBandeFocusedBandId = () => usePlanBandeDialogState().id;

const BandsDialogProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const { state, update } = useViewState<BandsDialogState>({});
    return <BandsDialogContext.Provider value={{ state, update }}>{children}</BandsDialogContext.Provider>;
};

export const useZoomLaizeRoomGesture = (id: string) => {
    const { state, update } = usePlanBandeDialog();
    const focused = state.id === id;
    const onMouseEnter = (e: GestureEvent) => update({ id });
    const clearFocusedBands = (e: GestureEvent) => update({ id: undefined });

    return { focused, gestures: { onMouseEnter, onMouseLeave: clearFocusedBands } };
};

export type MapBoundingBox = BoundingBox & { height: number; width: number };
export const calculateMapBoxWithPoints = (points: CoordPoint[]): MapBoundingBox | undefined => {
    const overallBBox = PointUtils.calculateBoundingBox({ points: points })!;
    if (!overallBBox) {
        return undefined;
    }
    const xMin = overallBBox.xMin;
    const yMin = overallBBox.yMin;
    const xMax = overallBBox.xMax;
    const yMax = overallBBox.yMax;
    const height = yMax - yMin;
    const width = xMax - xMin;
    return { xMin, yMin, xMax, yMax, height, width };
};

export const generateMapBandsStyles = (box: MapBoundingBox, legendItemsTopMargin = 0) => {
    const margin = LegendConstants.MARGIN;
    return {
        viewBox: {
            xMin: box.xMin,
            yMin: box.yMin,
            width: box.width + LegendConstants.LEGEND_WIDTH + margin * 2,
            height: box.height + margin + 124,
        },
        map: { transform: `translate(${LegendConstants.LEGEND_WIDTH}, ${124})` },
        legendTitle: { transform: `translate(${box.xMin + margin}, ${box.yMin + margin * 2})` },
        legendItems: { transform: `translate(${box.xMin + margin}, ${box.yMin + 74 + 19 + legendItemsTopMargin})` },
    };
};
